/**
 * Frame preloading for the time slider. Section 12.3.
 *
 * The policy is not invented here - the backend serves it on `/timesteps`,
 * reading `render.frame_stride_initial`, `frame_max_concurrent_requests` and
 * `frame_count_soft_cap` from settings.yaml. This module only executes it:
 *
 *   - the ACTIVE time-varying layer only, never every layer at once. Depth and
 *     velocity together would double the request count for a second animation
 *     nobody is looking at.
 *   - a coarse pass at `stride_initial` first, so scrubbing works within a few
 *     seconds, then a backfill of everything in between.
 *   - at most `max_concurrent_requests` in flight. Ninety-seven parallel image
 *     requests do not arrive sooner, they arrive in a worse order, and the
 *     browser queues them anyway.
 *   - never finer than `stride_min`, which is how a run with more frames than
 *     the soft cap stays inside the budget.
 *
 * Each frame costs one JSON call and one image load. The JSON call is not
 * overhead: it is what makes the server render that PNG.
 */

import { useEffect, useRef, useState } from 'react'

import { api, assetUrl } from './client'
import type { Overlay, Timesteps } from './types'

export interface FrameLegend {
  vmin: number
  vmax: number
  unit: string
  ramp: [number, string][]
}

export interface FrameCache {
  /** The overlay to draw for this index, falling back to the nearest loaded
   *  frame so the map never blanks mid-scrub. Null before the first arrives. */
  frameFor: (index: number) => Overlay | null
  /** True once the requested index itself is loaded, not a neighbour. */
  isExact: (index: number) => boolean
  loaded: number
  total: number
  /** The coarse pass is complete; scrubbing is usable. */
  scrubReady: boolean
  /** Every frame is in. */
  complete: boolean
  error: string | null
  bounds: [number, number, number, number] | null
  legend: FrameLegend | null
}

/** Coarse pass first, then everything else in time order. */
function loadOrder(count: number, strideInitial: number, strideMin: number): number[] {
  const eligible: number[] = []
  for (let i = 0; i < count; i += strideMin) eligible.push(i)

  const coarse: number[] = []
  const seen = new Set<number>()
  for (let i = 0; i < eligible.length; i += Math.max(1, Math.round(strideInitial / strideMin))) {
    coarse.push(eligible[i])
    seen.add(eligible[i])
  }
  // The last frame belongs in the coarse pass: dragging the slider to the end
  // is the first thing anyone does, and it is the one frame the stride is
  // almost guaranteed to miss.
  const last = eligible[eligible.length - 1]
  if (last !== undefined && !seen.has(last)) {
    coarse.push(last)
    seen.add(last)
  }

  return [...coarse, ...eligible.filter((i) => !seen.has(i))]
}

/** Warm the browser cache so MapLibre's own load is a cache hit. */
function preloadImage(url: string, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const image = new Image()
    const done = () => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }
    const onAbort = () => {
      image.src = ''
      done()
    }
    signal.addEventListener('abort', onAbort, { once: true })
    image.onload = done
    image.onerror = () => {
      signal.removeEventListener('abort', onAbort)
      reject(new Error(`Frame image failed to load: ${url}`))
    }
    image.src = url
  })
}

export function useFrames(
  runId: string | null,
  layer: string,
  timesteps: Timesteps | undefined,
): FrameCache {
  const framesRef = useRef(new Map<number, Overlay>())
  const [version, setVersion] = useState(0)
  const [scrubReady, setScrubReady] = useState(false)
  const [complete, setComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    framesRef.current = new Map()
    setVersion((v) => v + 1)
    setScrubReady(false)
    setComplete(false)
    setError(null)

    if (!runId || !timesteps || timesteps.count === 0) return

    const controller = new AbortController()
    const { signal } = controller
    const order = loadOrder(
      timesteps.count,
      timesteps.stride_initial,
      timesteps.stride_min,
    )
    const coarseCount = Math.max(
      1,
      Math.ceil(order.length / Math.max(1, timesteps.stride_initial / timesteps.stride_min)),
    )

    let cursor = 0
    let finished = 0

    async function worker() {
      while (cursor < order.length && !signal.aborted) {
        const index = order[cursor]
        cursor += 1
        try {
          const overlay = await api.overlay(runId as string, layer, index, signal)
          await preloadImage(assetUrl(overlay.png_url), signal)
          if (signal.aborted) return
          framesRef.current.set(index, overlay)
          setVersion((v) => v + 1)
        } catch (cause) {
          if (signal.aborted) return
          // One bad frame must not stop the other ninety-six. Report it and
          // keep going - a gap in the animation is recoverable, a dead slider
          // is not.
          setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
          finished += 1
          if (finished >= coarseCount) setScrubReady(true)
          if (finished >= order.length) setComplete(true)
        }
      }
    }

    const pool = Math.min(timesteps.max_concurrent_requests, order.length)
    void Promise.all(Array.from({ length: pool }, () => worker()))

    return () => controller.abort()
  }, [runId, layer, timesteps])

  // `version` is read so the returned closures are rebuilt when a frame lands;
  // without it React would keep handing the map a stale `frameFor`.
  void version

  const frames = framesRef.current

  return {
    frameFor: (index: number) => {
      const exact = frames.get(index)
      if (exact) return exact
      // Nearest loaded frame, preferring earlier: showing the flood slightly
      // behind is honest, showing it slightly ahead is not.
      let best: Overlay | null = null
      let bestDistance = Number.POSITIVE_INFINITY
      for (const [candidate, overlay] of frames) {
        const distance = Math.abs(candidate - index) + (candidate > index ? 0.5 : 0)
        if (distance < bestDistance) {
          bestDistance = distance
          best = overlay
        }
      }
      return best
    },
    isExact: (index: number) => frames.has(index),
    loaded: frames.size,
    total: timesteps?.count ?? 0,
    scrubReady,
    complete,
    error,
    bounds: frames.size > 0 ? [...frames.values()][0].bounds : null,
    legend:
      frames.size > 0
        ? (() => {
            const first = [...frames.values()][0]
            return {
              vmin: first.vmin,
              vmax: first.vmax,
              unit: first.unit,
              ramp: first.ramp,
            }
          })()
        : null,
  }
}
